import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PushNotificationToggle from '../components/PushNotificationToggle';

// Inline avatar upload
function AvatarUploadInline({ avatar, name, onSave }) {
 const [preview, setPreview] = useState(avatar || null);
 const [uploading, setUploading] = useState(false);
 const fileRef = useRef();
 const http = axios.create({ baseURL: '/api', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});

 useEffect(() => { setPreview(avatar); }, [avatar]);

 const handleFile = async (e) => {
 const file = e.target.files[0];
 if (!file) return;
 if (file.size > 500000) { alert('Max 500KB'); return; }
 const reader = new FileReader();
 reader.onload = async (ev) => {
 setPreview(ev.target.result);
 setUploading(true);
 try { await http.post('/account/avatar', { avatar_data: ev.target.result }); } catch { /* silent */ }
 setUploading(false);
 };
 reader.readAsDataURL(file);
 };

 const initials = (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

 return (
 <div className="flex flex-col items-center">
 <div className="relative group cursor-pointer" onClick={() => fileRef.current?.click()}>
 {preview ? (
 <img src={preview} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-brand-200 shadow-sm" />
 ) : (
 <div className="w-20 h-20 rounded-full bg-brand-100 flex items-center justify-center text-2xl font-bold text-brand-700 border-2 border-brand-200">
 {initials}
 </div>
 )}
 <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
 <span className="text-white text-xs font-bold">{uploading ? '...' : ''}</span>
 </div>
 <div className="absolute bottom-0 right-0 w-6 h-6 bg-brand-500 rounded-full border-2 border-white flex items-center justify-center">
 <span className="text-white text-[8px]"></span>
 </div>
 </div>
 <button onClick={() => fileRef.current?.click()} className="mt-2 text-xs text-brand-600 font-medium hover:underline">
 {preview ? 'Change photo' : 'Add profile photo'}
 </button>
 {!preview && <p className="text-[10px] text-neutral-400 mt-0.5">Profiles with photos get hired 3× more</p>}
 <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
 </div>
 );
}

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const getTabs = (role) => [
 ['profile', ' Profile'],
 ['security', ' Security'],
 ['payment', ' Payment'],
 ['workrights', ' Work Rights'],
 ...(role === 'worker' ? [['certifications', ' Certifications'], ['identity', '️ Identity']] : []),
 ...(role === 'employer' ? [['abn', ' ABN Verification']] : []),
 ['emergency', ' Emergency Contact'],
 ['notifications', ' Notifications'],
 ['privacy', '️ Privacy & Legal'],
];

function strengthLabel(pw) {
 if (!pw) return { label: '', color: '' };
 if (pw.length < 6) return { label: 'Weak', color: 'text-red-500' };
 if (pw.length < 10) return { label: 'Fair', color: 'text-amber-500' };
 if (/[A-Z]/.test(pw) && /[0-9]/.test(pw)) return { label: 'Strong', color: 'text-brand-600' };
 return { label: 'Good', color: 'text-blue-500' };
}

export default function SettingsPage({ user, onLogout }) {
 const navigate = useNavigate();
 const http = api();
 const [tab, setTab] = useState('profile');
 const [profile, setProfile] = useState({});
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [toast, setToast] = useState({ msg: '', type: 'ok' });

 // Security
 const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
 const [pwSaving, setPwSaving] = useState(false);

 // Delete
 const [deleteConfirm, setDeleteConfirm] = useState('');
 const [deletePassword, setDeletePassword] = useState('');
 const [deleting, setDeleting] = useState(false);

 useEffect(() => {
 loadSettings();
 }, []);

 const loadSettings = async () => {
 setLoading(true);
 try {
 const res = await http.get('/account/settings');
 setProfile(res.data);
 } catch { /* silent */ }
 setLoading(false);
 };

 const showToast = (msg, type = 'ok') => {
 setToast({ msg, type });
 setTimeout(() => setToast({ msg: '', type: 'ok' }), 4000);
 };

 const saveProfile = async (updates) => {
 setSaving(true);
 try {
 const res = await http.put('/account/settings', updates);
 setProfile(res.data);
 showToast('✓ Saved successfully');
 } catch (err) {
 showToast(err.response?.data?.error || 'Save failed', 'err');
 }
 setSaving(false);
 };

 const changePassword = async () => {
 if (pwForm.new_password !== pwForm.confirm) {
 showToast('Passwords do not match', 'err'); return;
 }
 if (pwForm.new_password.length < 8) {
 showToast('New password must be at least 8 characters', 'err'); return;
 }
 setPwSaving(true);
 try {
 await http.put('/account/password', {
 current_password: pwForm.current_password,
 new_password: pwForm.new_password
 });
 setPwForm({ current_password: '', new_password: '', confirm: '' });
 showToast('✓ Password updated successfully');
 } catch (err) {
 showToast(err.response?.data?.error || 'Password change failed', 'err');
 }
 setPwSaving(false);
 };

 const deleteAccount = async () => {
 if (deleteConfirm !== 'DELETE') {
 showToast('Type DELETE to confirm', 'err'); return;
 }
 setDeleting(true);
 try {
 await http.delete('/account', { data: { password: deletePassword } });
 localStorage.removeItem('token');
 localStorage.removeItem('user');
 if (onLogout) onLogout();
 navigate('/');
 } catch (err) {
 showToast(err.response?.data?.error || 'Delete failed', 'err');
 }
 setDeleting(false);
 };

 const strength = strengthLabel(pwForm.new_password);

 if (loading) return (
 <div className="flex items-center justify-center h-screen bg-neutral-50">
 <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
 </div>
 );

 return (
 <div className="min-h-screen bg-neutral-50">
 {/* Header */}
 <div className="bg-white border-b border-neutral-200 shadow-sm sticky top-0 z-10">
 <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <button onClick={() => navigate(-1)} className="text-neutral-400 hover:text-neutral-700 text-xl">←</button>
 <span className="font-bold text-neutral-900">Account Settings</span>
 </div>
 <span className="text-sm text-neutral-500">{profile.name}</span>
 </div>
 </div>

 <div className="max-w-4xl mx-auto px-4 py-6">
 {toast.msg && (
 <div className={`mb-4 p-3 rounded-xl text-sm border ${
 toast.type === 'err' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-brand-50 border-brand-200 text-brand-700'
 }`}>{toast.msg}</div>
 )}

 <div className="flex flex-col md:flex-row gap-6">
 {/* Sidebar */}
 <div className="md:w-48 shrink-0">
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm overflow-hidden">
 {getTabs(profile.role || user?.role).map(([id, label]) => (
 <button key={id} onClick={() => setTab(id)}
 className={`w-full text-left px-4 py-3 text-sm font-medium transition border-b border-neutral-50 last:border-0 ${
 tab === id ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-neutral-600 hover:bg-neutral-50'
 }`}>
 {label}
 </button>
 ))}
 </div>
 </div>

 {/* Content */}
 <div className="flex-1">

 {/* ── PROFILE ── */}
 {tab === 'profile' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-4">
 <h2 className="font-bold text-neutral-900 text-lg">Profile</h2>

 {/* Profile Photo */}
 <div className="flex justify-center pb-4 border-b border-neutral-100">
 <AvatarUploadInline avatar={profile.avatar_url} name={profile.name} onSave={saveProfile} />
 </div>
 {[
 { label: 'Full Name', key: 'name', type: 'text', placeholder: 'Your name' },
 { label: 'Phone', key: 'phone', type: 'tel', placeholder: '04xx xxx xxx' },
 { label: 'Location', key: 'location', type: 'text', placeholder: 'Sydney' },
 ].map(f => (
 <div key={f.key}>
 <label className="block text-sm font-medium text-neutral-700 mb-1">{f.label}</label>
 <input type={f.type} value={profile[f.key] || ''}
 onChange={e => setProfile({ ...profile, [f.key]: e.target.value })}
 placeholder={f.placeholder}
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
 </div>
 ))}
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Bio</label>
 <textarea rows={3} value={profile.bio || ''}
 onChange={e => setProfile({ ...profile, bio: e.target.value })}
 placeholder="Tell employers a little about yourself..."
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none" />
 </div>
 <button onClick={() => saveProfile({ name: profile.name, phone: profile.phone, location: profile.location, bio: profile.bio })}
 disabled={saving}
 className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-60">
 {saving ? 'Saving…' : 'Save Profile'}
 </button>
 </div>
 )}

 {/* ── SECURITY ── */}
 {tab === 'security' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-4">
 <h2 className="font-bold text-neutral-900 text-lg">Change Password</h2>
 {[
 { label: 'Current Password', key: 'current_password' },
 { label: 'New Password', key: 'new_password' },
 { label: 'Confirm New Password', key: 'confirm' },
 ].map(f => (
 <div key={f.key}>
 <label className="block text-sm font-medium text-neutral-700 mb-1">{f.label}</label>
 <input type="password" value={pwForm[f.key]}
 onChange={e => setPwForm({ ...pwForm, [f.key]: e.target.value })}
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
 {f.key === 'new_password' && pwForm.new_password && (
 <p className={`text-xs mt-1 font-medium ${strength.color}`}>Strength: {strength.label}</p>
 )}
 </div>
 ))}
 <button onClick={changePassword} disabled={pwSaving}
 className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-60">
 {pwSaving ? 'Updating…' : 'Update Password'}
 </button>
 </div>
 )}

 {/* ── PAYMENT ── */}
 {tab === 'payment' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-4">
 <div>
 <h2 className="font-bold text-neutral-900 text-lg">Your Earnings Go Here</h2>
 <p className="text-sm text-neutral-500 mt-1">Payments processed within 24h of shift completion.</p>
 </div>
 {profile.bank_account && (
 <div className="flex items-center gap-2 px-4 py-2 bg-brand-50 border border-brand-200 rounded-xl text-brand-700 text-sm font-semibold">
 ✓ Payment details saved
 </div>
 )}
 {[
 { label: 'Bank Name', key: 'bank_name', placeholder: 'e.g. Commonwealth Bank' },
 { label: 'BSB', key: 'bank_bsb', placeholder: '000-000' },
 { label: 'Account Number', key: 'bank_account', placeholder: 'XXXXXXXX' },
 ].map(f => (
 <div key={f.key}>
 <label className="block text-sm font-medium text-neutral-700 mb-1">{f.label}</label>
 <input type="text" value={profile[f.key] || ''}
 onChange={e => setProfile({ ...profile, [f.key]: e.target.value })}
 placeholder={f.placeholder}
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
 </div>
 ))}
 <button onClick={() => saveProfile({ bank_name: profile.bank_name, bank_bsb: profile.bank_bsb, bank_account: profile.bank_account })}
 disabled={saving}
 className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-60">
 {saving ? 'Saving…' : 'Save Payment Details'}
 </button>
 </div>
 )}

 {/* ── WORK RIGHTS ── */}
 {tab === 'workrights' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-5">
 <h2 className="font-bold text-neutral-900 text-lg">Work Rights & Tax</h2>

 <label className="flex items-start gap-3 cursor-pointer">
 <input type="checkbox" checked={!!profile.right_to_work_confirmed}
 onChange={e => setProfile({ ...profile, right_to_work_confirmed: e.target.checked })}
 className="mt-1 w-4 h-4 accent-brand-500" />
 <span className="text-sm text-neutral-700">
 I confirm I have the legal right to work in Australia. I understand that providing false information may result in immediate removal from the platform.
 {profile.right_to_work_confirmed_at && (
 <span className="ml-1 text-xs text-neutral-400">
 (Confirmed {new Date(profile.right_to_work_confirmed_at).toLocaleDateString('en-AU')})
 </span>
 )}
 </span>
 </label>

 <label className="flex items-center gap-3 cursor-pointer">
 <input type="checkbox" checked={!!profile.tfn_provided}
 onChange={e => setProfile({ ...profile, tfn_provided: e.target.checked })}
 className="w-4 h-4 accent-brand-500" />
 <span className="text-sm text-neutral-700">I have provided my Tax File Number (TFN) to employers</span>
 </label>

 <div className="border-t border-neutral-100 pt-4">
 <p className="text-sm font-semibold text-neutral-700 mb-3">Superannuation Details</p>
 {[
 { label: 'Super Fund BSB', key: 'super_bsb', placeholder: 'BSB' },
 { label: 'Super Account Number', key: 'super_account', placeholder: 'Account number' },
 ].map(f => (
 <div key={f.key} className="mb-3">
 <label className="block text-sm font-medium text-neutral-700 mb-1">{f.label}</label>
 <input type="text" value={profile[f.key] || ''}
 onChange={e => setProfile({ ...profile, [f.key]: e.target.value })}
 placeholder={f.placeholder}
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
 </div>
 ))}
 </div>

 <div className="border-t border-neutral-100 pt-4 space-y-3">
 <p className="text-sm font-semibold text-neutral-700">Legal Agreements</p>
 {(profile.role || user?.role) === 'worker' && (
 <label className="flex items-start gap-3 cursor-pointer">
 <input type="checkbox" checked={!!profile.contractor_agreement_accepted}
 onChange={e => setProfile({ ...profile, contractor_agreement_accepted: e.target.checked })}
 className="mt-1 w-4 h-4 accent-brand-500" />
 <span className="text-sm text-neutral-700">I am engaging as an <strong>independent contractor</strong>, responsible for my own tax and superannuation.</span>
 </label>
 )}
 {(profile.role || user?.role) === 'employer' && (
 <label className="flex items-start gap-3 cursor-pointer">
 <input type="checkbox" checked={!!profile.whs_acknowledged}
 onChange={e => setProfile({ ...profile, whs_acknowledged: e.target.checked })}
 className="mt-1 w-4 h-4 accent-brand-500" />
 <span className="text-sm text-neutral-700">I acknowledge my WHS obligations as a PCBU under the Work Health & Safety Act 2011.</span>
 </label>
 )}
 <label className="flex items-center gap-3 cursor-pointer">
 <input type="checkbox" checked={!!profile.gst_registered}
 onChange={e => setProfile({ ...profile, gst_registered: e.target.checked })}
 className="w-4 h-4 accent-brand-500" />
 <span className="text-sm text-neutral-700">I am registered for GST (required if earning over $75,000/year)</span>
 </label>
 </div>

 <button onClick={() => saveProfile({
 right_to_work_confirmed: profile.right_to_work_confirmed,
 tfn_provided: profile.tfn_provided,
 super_bsb: profile.super_bsb,
 super_account: profile.super_account,
 contractor_agreement_accepted: profile.contractor_agreement_accepted,
 whs_acknowledged: profile.whs_acknowledged,
 gst_registered: profile.gst_registered,
 })} disabled={saving}
 className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-60">
 {saving ? 'Saving…' : 'Save Work Rights'}
 </button>

 <div className="mt-2 pt-4 border-t border-neutral-100">
 <a href="/compliance" className="inline-flex items-center gap-2 text-sm text-brand-600 hover:underline font-medium"> View Full Compliance Status →</a>
 </div>
 </div>
 )}

 {/* ── CERTIFICATIONS (worker) ── */}
 {tab === 'certifications' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6">
 <h2 className="text-lg font-semibold text-neutral-900 mb-3"> My Certifications</h2>
 <p className="text-sm text-neutral-500 mb-4">Manage your professional certifications. Verified certs are shown to employers when you apply for shifts.</p>
 <button onClick={() => navigate('/certifications')} className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-lg transition">
 Manage Certifications →
 </button>
 </div>
 )}

 {/* ── ABN VERIFICATION (employer) ── */}
 {tab === 'abn' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6">
 <h2 className="text-lg font-semibold text-neutral-900 mb-3"> ABN Verification</h2>
 <p className="text-sm text-neutral-500 mb-4">Verify your Australian Business Number to get a trust badge and attract more workers.</p>
 <button onClick={() => navigate('/abn-verification')} className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-lg transition">
 Verify ABN →
 </button>
 </div>
 )}

 {/* ── EMERGENCY CONTACT ── */}
 {tab === 'emergency' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-4">
 <div>
 <h2 className="font-bold text-neutral-900 text-lg">Emergency Contact</h2>
 <p className="text-sm text-neutral-500 mt-1">Only contacted in case of a workplace emergency.</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Contact Name</label>
 <input type="text" value={profile.emergency_contact_name || ''}
 onChange={e => setProfile({ ...profile, emergency_contact_name: e.target.value })}
 placeholder="Full name"
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Contact Phone</label>
 <input type="tel" value={profile.emergency_contact_phone || ''}
 onChange={e => setProfile({ ...profile, emergency_contact_phone: e.target.value })}
 placeholder="04xx xxx xxx"
 className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
 </div>
 <button onClick={() => saveProfile({ emergency_contact_name: profile.emergency_contact_name, emergency_contact_phone: profile.emergency_contact_phone })}
 disabled={saving}
 className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-60">
 {saving ? 'Saving…' : 'Save Emergency Contact'}
 </button>
 </div>
 )}

 {/* ── NOTIFICATIONS ── */}
 {tab === 'notifications' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-5">
 <h2 className="font-bold text-neutral-900 text-lg">Notifications</h2>
 <PushNotificationToggle />
 {[
 { label: 'Email notifications', key: 'notifications_email', desc: 'Get shift updates and hiring decisions by email' },
 { label: 'SMS notifications', key: 'notifications_sms', desc: 'Get instant SMS when you\'re hired or a shift is confirmed' },
 ].map(n => (
 <div key={n.key} className="flex items-start justify-between gap-4 p-4 border border-neutral-100 rounded-xl">
 <div>
 <p className="font-medium text-neutral-900 text-sm">{n.label}</p>
 <p className="text-xs text-neutral-500 mt-0.5">{n.desc}</p>
 </div>
 <button onClick={() => {
 const updated = { ...profile, [n.key]: !profile[n.key] };
 setProfile(updated);
 saveProfile({ [n.key]: !profile[n.key] });
 }}
 className={`shrink-0 w-12 h-6 rounded-full transition-colors relative ${
 profile[n.key] !== false ? 'bg-brand-500' : 'bg-neutral-200'
 }`}>
 <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
 profile[n.key] !== false ? 'left-6' : 'left-0.5'
 }`} />
 </button>
 </div>
 ))}
 </div>
 )}

 {/* ── PRIVACY & LEGAL ── */}
 {tab === 'privacy' && (
 <div className="space-y-5">
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 space-y-4">
 <h2 className="font-bold text-neutral-900 text-lg">Privacy & Legal</h2>
 <div className="flex flex-col gap-3">
 <a href="https://shiftmatch.work/privacy" target="_blank" rel="noreferrer"
 className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium">
 Privacy Policy →
 </a>
 <a href="https://shiftmatch.work/terms" target="_blank" rel="noreferrer"
 className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium">
 Terms of Service →
 </a>
 <button className="flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700 font-medium text-left" onClick={() => showToast('Data export request received — you\'ll get an email within 48h.')}>
 Download my data
 </button>
 </div>
 </div>

 {/* Sign out */}
 <button onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); if (onLogout) onLogout(); }}
   className="w-full py-3 text-sm font-semibold text-neutral-500 hover:text-rose-500 bg-neutral-50 hover:bg-rose-50 rounded-2xl border border-neutral-200 hover:border-rose-200 transition-all">
   Sign out
 </button>

 {/* Danger zone */}
 <div className="bg-white rounded-2xl border-2 border-red-200 p-6 space-y-4">
 <div className="flex items-center gap-2">
 <span className="text-red-500 text-lg">⚠️</span>
 <h3 className="font-bold text-red-700">Delete Account</h3>
 </div>
 <p className="text-sm text-neutral-600">This will permanently delete your account and all data. This cannot be undone.</p>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Type <strong>DELETE</strong> to confirm</label>
 <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
 placeholder="DELETE"
 className="w-full border border-red-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent" />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1">Your password</label>
 <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
 placeholder="Enter your password"
 className="w-full border border-red-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent" />
 </div>
 <button onClick={deleteAccount} disabled={deleting || deleteConfirm !== 'DELETE' || !deletePassword}
 className="px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition disabled:opacity-40">
 {deleting ? 'Deleting…' : 'Delete My Account'}
 </button>
 </div>
 </div>
 )}

 </div>

 {/* Identity tab */}
 {tab === 'identity' && (
 <div className="space-y-5">
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6">
 <h2 className="font-bold text-neutral-900 text-lg mb-4">️ Identity Verification</h2>
 <IdentityStatusPanel http={http} />
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

function IdentityStatusPanel({ http }) {
 const [status, setStatus] = React.useState(null);
 React.useEffect(() => {
 http.get('/identity/status').then(r => setStatus(r.data)).catch(() => {});
 }, []);

 if (!status) return <div className="text-neutral-400 text-sm">Loading...</div>;

 if (status.verified || status.status === 'verified') {
 return (
 <div>
 <div className="flex items-center gap-3 mb-4">
 <div className="w-12 h-12 bg-brand-50 rounded-full flex items-center justify-center text-2xl">️</div>
 <div>
 <p className="font-bold text-brand-700">Identity Verified ✓</p>
 <p className="text-xs text-neutral-500">{status.verified_at ? `Verified ${new Date(status.verified_at).toLocaleDateString('en-AU')}` : 'Verified'}</p>
 </div>
 </div>
 <div className="space-y-2 text-sm">
 <div className="flex items-center gap-2 text-brand-700"><span>✓</span><span>Document 1: {status.doc_type_1 || 'Verified'}</span></div>
 <div className="flex items-center gap-2 text-brand-700"><span>✓</span><span>Document 2: {status.doc_type_2 || 'Verified'}</span></div>
 <div className="flex items-center gap-2 text-brand-700"><span>✓</span><span>Selfie / Liveness check passed</span></div>
 </div>
 </div>
 );
 }

 if (status.status === 'pending') {
 return (
 <div className="text-center py-4">
 <div className="text-4xl mb-3">⏳</div>
 <p className="font-semibold text-neutral-800 mb-1">Verification In Progress</p>
 <p className="text-sm text-neutral-500">Your documents are being reviewed. Usually approved within minutes.</p>
 </div>
 );
 }

 return (
 <div>
 <p className="text-sm text-neutral-600 mb-4">Your identity hasn't been verified yet. Verified workers get hired 3× faster.</p>
 <a href="/identity-verification"
 className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition text-sm">
 ️ Verify My Identity →
 </a>
 </div>
 );
}
